import type { PDFDocumentLoadingTask, PDFWorker, RenderTask } from "pdfjs-dist";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { createRecordPdf, downloadRecordPdf, type RecordPdf, type RecordReport } from "./lib/recordExports";
import { printRecordPdf } from "./lib/recordPrint";
import RecordActionButton from "./RecordActionButton";
import "./record-pdf-preview.css";

export default function RecordPdfPreview({
  report,
  onBusyChange,
  onBack,
}: {
  report: RecordReport;
  onBusyChange: (busy: boolean) => void;
  onBack?: () => void;
}) {
  const { t } = useTranslation("localExport");
  const pages = useRef<HTMLDivElement>(null);
  const rendered = useRef<Array<{ canvas: HTMLCanvasElement; width: number; height: number }>>([]);
  const [ready, setReady] = useState(false);
  const [file, setFile] = useState<RecordPdf | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    let loading: PDFDocumentLoadingTask | undefined;
    let rendering: RenderTask | undefined;
    let pdfWorker: PDFWorker | undefined;
    let worker: Worker | undefined;
    function dispose() {
      rendering?.cancel();
      void loading?.destroy().catch(() => {});
      pdfWorker?.destroy();
      worker?.terminate();
    }
    const host = pages.current;
    host?.replaceChildren();
    rendered.current = [];
    setReady(false);
    setFile(null);
    setError("");
    setNotice("");
    async function render() {
      try {
        const pdfFile = await createRecordPdf(report);
        if (cancelled) return;
        const pdfjs = await import("pdfjs-dist");
        const workerUrl = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        const data = new Uint8Array(await pdfFile.blob.arrayBuffer());
        if (cancelled) return;
        // Own the worker so a failed load can be retried without PDF.js's cached fallback failure.
        worker = new Worker(workerUrl.default, { type: "module" });
        const workerFailed = new Promise<never>((_, reject) => {
          worker?.addEventListener(
            "error",
            (event) => {
              event.preventDefault();
              reject(new Error("pdf-worker"));
            },
            { once: true },
          );
        });
        pdfWorker = pdfjs.PDFWorker.create({ port: worker });
        loading = pdfjs.getDocument({ data, worker: pdfWorker, isEvalSupported: false });
        const pdf = await Promise.race([loading.promise, workerFailed]);
        for (let number = 1; number <= pdf.numPages; number++) {
          const page = await Promise.race([pdf.getPage(number), workerFailed]);
          if (cancelled) return;
          const size = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.setAttribute("role", "img");
          const label = t("pdfPage", { page: number, count: pdf.numPages });
          canvas.setAttribute("aria-label", label);
          rendering = page.render({ canvas, viewport });
          await Promise.race([rendering.promise, workerFailed]);
          if (cancelled) return;
          const figure = document.createElement("figure");
          const caption = document.createElement("figcaption");
          caption.textContent = label;
          figure.append(caption, canvas);
          host?.append(figure);
          rendered.current.push({ canvas, width: size.width, height: size.height });
        }
        if (!cancelled) {
          setFile(pdfFile);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          dispose();
          setError(t("pdfPreviewFailed"));
        }
      }
    }
    void render();
    return () => {
      cancelled = true;
      dispose();
      rendered.current = [];
      host?.replaceChildren();
    };
  }, [report, attempt, t]);

  async function print() {
    if (!file || !ready || printing) return;
    setPrinting(true);
    onBusyChange(true);
    setError("");
    setNotice("");
    try {
      await printRecordPdf(
        rendered.current.map(({ canvas, width, height }) => ({ image: canvas.toDataURL("image/png"), width, height })),
        file.filename,
      );
      setNotice(t("printOpened"));
    } catch {
      setError(t("printFailed"));
    } finally {
      setPrinting(false);
      onBusyChange(false);
    }
  }

  return (
    <>
      <div className="record-pdf-actions">
        {onBack && (
          <button type="button" disabled={printing} onClick={onBack}>
            {t("backToExport")}
          </button>
        )}
        {!ready && error && (
          <button type="button" onClick={() => setAttempt(attempt + 1)}>
            {t("retryPdf")}
          </button>
        )}
        <RecordActionButton
          action="export"
          disabled={!ready || printing}
          onClick={() => {
            if (!file) return;
            downloadRecordPdf(file);
            setNotice(t("pdfDownloadStarted"));
          }}
        >
          {t("downloadPdf")}
        </RecordActionButton>
        <RecordActionButton action="print" disabled={!ready || printing} onClick={() => void print()} />
      </div>
      {!ready && !error && <p role="status">{t("pdfLoading")}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      <div
        ref={pages}
        className="record-pdf-pages"
        role="region"
        aria-label={t("pdfPreview")}
        aria-busy={!ready && !error}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard focus permits scrolling all PDF pages.
        tabIndex={0}
      />
    </>
  );
}
